import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
View,
Text,
StyleSheet,
ScrollView,
TextInput,
TouchableOpacity,
ActivityIndicator,
Image,
Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_BASE = 'https://collabbay.com/APIControllers';
const ASSET_BASE = 'https://collabbayadmin.com';

const FIXED_PRICE = '5';
const FIXED_PRICE_PENCE = 500;
const STRIPE_CURRENCY = 'gbp';

/*
This is your public Stripe publishable key.
It is safe and required to exist in frontend code.
Never place your Stripe secret key or webhook secret in this file.
*/
const STRIPE_PUBLISHABLE_KEY =
'pk_live_51LFKahH6hLpSC9J6BdtcSRXdiDv91C7dTG5VFide0ZJhB0iPVV7BgAFAIa9VNdBZ3QWNpWwsjL1dx7GNY5cSp06D00Hq8HTQYh';

const STRIPE_SCRIPT_URL = 'https://js.stripe.com/v3/';
const CARD_ELEMENT_ID = 'voucher-stripe-card-element';
const APPLE_PAY_ELEMENT_ID = 'voucher-apple-pay-element';

const isValidEmail = (value) => {
const cleaned = String(value || '').trim();
return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned);
};

const normaliseEmail = (value) => String(value || '').trim().toLowerCase();

const loadStripeScript = () =>
new Promise((resolve, reject) => {
if (Platform.OS !== 'web') {
reject(new Error('Stripe checkout is available on web only.'));
return;
}

if (window.Stripe) {
  resolve(window.Stripe);
  return;
}

const existingScript = document.querySelector(
  `script[src="${STRIPE_SCRIPT_URL}"]`
);  

if (existingScript) {
  existingScript.addEventListener('load', () => {
    if (window.Stripe) {
      resolve(window.Stripe);
    } else {
      reject(new Error('Stripe could not be loaded.'));
    }
  });

  existingScript.addEventListener('error', () => {
    reject(new Error('Stripe could not be loaded.'));
  });

  return;
}

const script = document.createElement('script');
script.src = STRIPE_SCRIPT_URL;
script.async = true;

script.onload = () => {
  if (window.Stripe) {
    resolve(window.Stripe);
  } else {
    reject(new Error('Stripe could not be loaded.'));
  }
};

script.onerror = () => {
  reject(new Error('Stripe could not be loaded.'));
};

document.head.appendChild(script);

});

const Voucher = ({ route }) => {
const CollabID =
route?.params?.CollabID ||
route?.params?.collabID ||
route?.params?.collabId ||
route?.params?.CollabId ||
route?.params?.id ||
'';

const [loading, setLoading] = useState(true);
const [voucher, setVoucher] = useState(null);
const [postUpload, setPostUpload] = useState(null);
const [contentMedia, setContentMedia] = useState(null);
const [loadError, setLoadError] = useState(null);

const [CustomerEmail, setCustomerEmail] = useState('');

const [stripeReady, setStripeReady] = useState(false);
const [applePayAvailable, setApplePayAvailable] = useState(false);
const [cardComplete, setCardComplete] = useState(false);
const [processing, setProcessing] = useState(false);
const [paymentError, setPaymentError] = useState('');

const [paymentSuccess, setPaymentSuccess] = useState(false);
const [paymentStatus, setPaymentStatus] = useState('');
const [paidEmail, setPaidEmail] = useState('');

const stripeRef = useRef(null);
const cardElementRef = useRef(null);
const paymentRequestRef = useRef(null);
const paymentRequestButtonRef = useRef(null);
const customerEmailRef = useRef('');
const paymentAttemptActiveRef = useRef(false);

const stockNumber = Number(voucher?.Stock || 0);
const soldOut = voucher ? stockNumber <= 0 : false;

useEffect(() => {
customerEmailRef.current = CustomerEmail;
}, [CustomerEmail]);

useEffect(() => {
fetchVoucherInformation();
}, [CollabID]);

const normalizeAssetUrl = (value) => {
if (!value) return null;

const raw = String(value).trim();

if (!raw) return null;

if (raw.startsWith('http://') || raw.startsWith('https://')) {
  return raw;
}

if (raw.startsWith('//')) {
  return `https:${raw}`;
}

if (raw.startsWith('/')) {
  return `${ASSET_BASE}${raw}`;
}

return `${ASSET_BASE}/${raw}`;

};

const getInitials = (value) => {
if (!value) return '?';

const cleanValue = String(value).trim();

if (!cleanValue) return '?';

const parts = cleanValue.split(' ').filter(Boolean);

if (parts.length === 1) {
  return parts[0].substring(0, 2).toUpperCase();
}

return `${parts[0][0]}${parts[1][0]}`.toUpperCase();

};

const getMediaTypeFromUrl = (url) => {
const cleanUrl = String(url || '').split('?')[0].toLowerCase();

if (
  cleanUrl.endsWith('.mp4') ||
  cleanUrl.endsWith('.mov') ||
  cleanUrl.endsWith('.webm') ||
  cleanUrl.endsWith('.m4v')
) {
  return 'video';
}

if (
  cleanUrl.endsWith('.jpg') ||
  cleanUrl.endsWith('.jpeg') ||
  cleanUrl.endsWith('.png') ||
  cleanUrl.endsWith('.gif') ||
  cleanUrl.endsWith('.webp') ||
  cleanUrl.endsWith('.avif')
) {
  return 'image';
}

return 'link';

};

const displayedMedia = useMemo(() => {
if (contentMedia?.url) {
const finalUrl = normalizeAssetUrl(contentMedia.url);

  return {
    url: finalUrl,
    type: contentMedia.type || getMediaTypeFromUrl(finalUrl),
  };
}

if (postUpload?.Image) {
  const finalUrl = normalizeAssetUrl(postUpload.Image);

  return {
    url: finalUrl,
    type: getMediaTypeFromUrl(finalUrl),
  };
}

return null;

}, [contentMedia, postUpload]);

const fetchVoucherInformation = async () => {
try {
setLoading(true);
setLoadError(null);
setVoucher(null);

  if (!CollabID) {
    setLoadError({
      title: 'Missing CollabID',
      message: 'No CollabID was passed into this screen.',
      raw: route?.params || {},
    });
    return;
  }

  const url = `${API_BASE}/FetchVoucherInformation.php?CollabID=${encodeURIComponent(
    CollabID
  )}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  const rawText = await response.text();

  let data;

  try {
    data = JSON.parse(rawText);
  } catch (parseError) {
    setLoadError({
      title: 'Invalid PHP response',
      message: 'The PHP file did not return valid JSON.',
      raw: rawText,
    });
    return;
  }

  if (data.success) {
    setVoucher(data.voucher);
    setPostUpload(data.postUpload || null);
    setContentMedia(data.contentMedia || null);
    setLoadError(null);
  } else {
    setLoadError({
      title: data.message || 'Voucher could not be loaded',
      message:
        data.error ||
        data.message ||
        'The server returned success:false.',
      raw: data,
    });
  }
} catch (error) {
  setLoadError({
    title: 'Network error',
    message:
      error?.message || 'Something went wrong loading this voucher.',
    raw: String(error),
  });
} finally {
  setLoading(false);
}

};

const createVoucherPaymentIntent = async (emailAddress) => {
const response = await fetch('https://collabbay.com/vouchercharge.php', {
method: 'POST',
headers: {
'Content-Type': 'application/json',
Accept: 'application/json',
},
body: JSON.stringify({
CollabID,
CustomerEmail: normaliseEmail(emailAddress),
}),
});

const rawText = await response.text();

let data;

try {
  data = JSON.parse(rawText);
} catch (parseError) {
  console.error('VoucherCharge.php raw response:', rawText);
  throw new Error('The payment server returned invalid JSON.');
}

if (!response.ok || !data.success || !data.clientSecret) {
  throw new Error(
    data.message || 'Could not start your voucher payment.'
  );
}

return data.clientSecret;

};

const completePaymentOnScreen = (status, emailAddress) => {
setPaymentStatus(status);
setPaidEmail(normaliseEmail(emailAddress));
setPaymentSuccess(true);
setPaymentError('');
};

const handleCardPayment = async () => {
if (!voucher) {
setPaymentError('Voucher not loaded.');
return;
}

if (soldOut) {
  setPaymentError('This voucher is sold out.');
  return;
}

const cleanedEmail = normaliseEmail(CustomerEmail);

if (!cleanedEmail) {
  setPaymentError('Please enter your email address.');
  return;
}

if (!isValidEmail(cleanedEmail)) {
  setPaymentError('Please enter a valid email address.');
  return;
}

if (!stripeRef.current || !cardElementRef.current || !stripeReady) {
  setPaymentError('Secure payment is still loading. Please try again.');
  return;
}

if (!cardComplete) {
  setPaymentError('Please enter your complete card details.');
  return;
}

if (paymentAttemptActiveRef.current) {
  return;
}

try {
  paymentAttemptActiveRef.current = true;
  setProcessing(true);
  setPaymentError('');

  const clientSecret = await createVoucherPaymentIntent(cleanedEmail);

  const result = await stripeRef.current.confirmCardPayment(
    clientSecret,
    {
      payment_method: {
        card: cardElementRef.current,
        billing_details: {
          email: cleanedEmail,
        },
      },
    }
  );

  if (result.error) {
    throw new Error(
      result.error.message || 'Your card payment could not be completed.'
    );
  }

  if (
    result.paymentIntent &&
    (result.paymentIntent.status === 'succeeded' ||
      result.paymentIntent.status === 'processing')
  ) {
    completePaymentOnScreen(result.paymentIntent.status, cleanedEmail);
    return;
  }

  throw new Error(
    'Your payment has not completed. Please try again.'
  );
} catch (error) {
  console.error('Voucher card payment error:', error);
  setPaymentError(
    error?.message || 'Something went wrong completing your payment.'
  );
} finally {
  paymentAttemptActiveRef.current = false;
  setProcessing(false);
}

};

useEffect(() => {
let cancelled = false;
let mountedCardElement = null;
let mountedPaymentRequestButton = null;
let mountedPaymentRequest = null;
let applePayHandler = null;

const initialiseStripeCheckout = async () => {
  if (Platform.OS !== 'web') {
    setPaymentError('Secure checkout is currently available on web only.');
    return;
  }

  if (!voucher || soldOut || paymentSuccess) {
    return;
  }

  try {
    setStripeReady(false);
    setApplePayAvailable(false);
    setCardComplete(false);
    setPaymentError('');

    const StripeConstructor = await loadStripeScript();

    if (cancelled) {
      return;
    }

    const stripe = StripeConstructor(STRIPE_PUBLISHABLE_KEY);
    const elements = stripe.elements({
      locale: 'en-GB',
    });

    stripeRef.current = stripe;

    const cardElement = elements.create('card', {
      hidePostalCode: false,
      style: {
        base: {
          color: '#111111',
          fontSize: '15px',
          fontFamily: 'Arial, sans-serif',
          fontSmoothing: 'antialiased',
          '::placeholder': {
            color: '#999999',
          },
        },
        invalid: {
          color: '#c62828',
          iconColor: '#c62828',
        },
      },
    });

    mountedCardElement = cardElement;
    cardElementRef.current = cardElement;

    const cardMountNode = document.getElementById(CARD_ELEMENT_ID);

    if (!cardMountNode) {
      throw new Error('Payment card field could not be loaded.');
    }

    cardElement.mount(`#${CARD_ELEMENT_ID}`);

    cardElement.on('change', (event) => {
      if (cancelled) {
        return;
      }

      setCardComplete(Boolean(event.complete));

      if (event.error) {
        setPaymentError(event.error.message);
      } else {
        setPaymentError('');
      }
    });

    setStripeReady(true);

    const paymentRequest = stripe.paymentRequest({
      country: 'GB',
      currency: STRIPE_CURRENCY,
      total: {
        label: `${voucher.BrandName} voucher`,
        amount: FIXED_PRICE_PENCE,
      },
      requestPayerEmail: true,
    });

    mountedPaymentRequest = paymentRequest;
    paymentRequestRef.current = paymentRequest;

    applePayHandler = async (event) => {
      let applePayCompleted = false;

      const completeApplePay = (status) => {
        if (!applePayCompleted) {
          event.complete(status);
          applePayCompleted = true;
        }
      };

      if (paymentAttemptActiveRef.current) {
        completeApplePay('fail');
        return;
      }

      const walletEmail = normaliseEmail(
        event.payerEmail || customerEmailRef.current
      );

      if (!isValidEmail(walletEmail)) {
        completeApplePay('fail');
        setPaymentError(
          'Apple Pay did not return a valid email address for voucher delivery.'
        );
        return;
      }

      try {
        paymentAttemptActiveRef.current = true;
        setProcessing(true);
        setPaymentError('');
        setCustomerEmail(walletEmail);

        const clientSecret = await createVoucherPaymentIntent(walletEmail);

        let result = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: event.paymentMethod.id,
          },
          {
            handleActions: false,
          }
        );

        if (result.error) {
          completeApplePay('fail');
          throw new Error(
            result.error.message || 'Apple Pay could not be completed.'
          );
        }

        completeApplePay('success');

        if (
          result.paymentIntent &&
          result.paymentIntent.status === 'requires_action'
        ) {
          result = await stripe.confirmCardPayment(clientSecret);

          if (result.error) {
            throw new Error(
              result.error.message || 'Apple Pay authentication failed.'
            );
          }
        }

        if (
          result.paymentIntent &&
          (result.paymentIntent.status === 'succeeded' ||
            result.paymentIntent.status === 'processing')
        ) {
          completePaymentOnScreen(result.paymentIntent.status, walletEmail);
          return;
        }

        throw new Error(
          'Your Apple Pay payment has not completed. Please try again.'
        );
      } catch (error) {
        completeApplePay('fail');
        console.error('Voucher Apple Pay error:', error);
        setPaymentError(
          error?.message ||
            'Something went wrong completing your Apple Pay payment.'
        );
      } finally {
        paymentAttemptActiveRef.current = false;
        setProcessing(false);
      }
    };

    paymentRequest.on('paymentmethod', applePayHandler);

    const canMakePayment = await paymentRequest.canMakePayment();

    if (cancelled) {
      return;
    }

    if (canMakePayment && canMakePayment.applePay) {
      const paymentRequestButton = elements.create(
        'paymentRequestButton',
        {
          paymentRequest,
          style: {
            paymentRequestButton: {
              type: 'buy',
              theme: 'dark',
              height: '48px',
            },
          },
        }
      );

      mountedPaymentRequestButton = paymentRequestButton;
      paymentRequestButtonRef.current = paymentRequestButton;

      paymentRequestButton.mount(`#${APPLE_PAY_ELEMENT_ID}`);
      setApplePayAvailable(true);
    }
  } catch (error) {
    console.error('Stripe checkout initialisation error:', error);

    if (!cancelled) {
      setStripeReady(false);
      setPaymentError(
        error?.message || 'Secure payment could not be loaded.'
      );
    }
  }
};

initialiseStripeCheckout();

return () => {
  cancelled = true;

  try {
    if (
      mountedPaymentRequest &&
      applePayHandler &&
      typeof mountedPaymentRequest.off === 'function'
    ) {
      mountedPaymentRequest.off('paymentmethod', applePayHandler);
    }
  } catch (error) {
    console.error('Apple Pay listener cleanup error:', error);
  }

  try {
    if (mountedPaymentRequestButton) {
      mountedPaymentRequestButton.unmount();
    }
  } catch (error) {
    console.error('Apple Pay element cleanup error:', error);
  }

  try {
    if (mountedCardElement) {
      mountedCardElement.unmount();
    }
  } catch (error) {
    console.error('Card element cleanup error:', error);
  }

  stripeRef.current = null;
  cardElementRef.current = null;
  paymentRequestRef.current = null;
  paymentRequestButtonRef.current = null;
};

}, [CollabID, voucher, paymentSuccess, soldOut]);

const renderAvatar = (imageUrl, label, customStyle) => {
const finalImageUrl = normalizeAssetUrl(imageUrl);

return (
  <View style={[styles.avatarOuter, customStyle]}>
    {finalImageUrl ? (
      <Image
        source={{ uri: finalImageUrl }}
        style={styles.avatarImage}
        resizeMode="cover"
      />
    ) : (
      <View style={styles.avatarFallback}>
        <Text style={styles.avatarFallbackText}>{getInitials(label)}</Text>
      </View>
    )}
  </View>
);

};

const renderRealGradientFade = () => {
if (Platform.OS === 'web') {
return React.createElement('div', {
style: {
position: 'absolute',
left: 0,
right: 0,
bottom: 0,
height: '78%',
pointerEvents: 'none',
background:
'linear-gradient(to top, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.82) 22%, rgba(0,0,0,0.50) 48%, rgba(0,0,0,0.18) 72%, rgba(0,0,0,0.00) 100%)',
},
});
}

return (
  <View pointerEvents="none" style={styles.nativeFadeWrapper}>
    <View style={[styles.nativeFadeLayer, styles.nativeFadeLayerOne]} />
    <View style={[styles.nativeFadeLayer, styles.nativeFadeLayerTwo]} />
    <View style={[styles.nativeFadeLayer, styles.nativeFadeLayerThree]} />
    <View style={[styles.nativeFadeLayer, styles.nativeFadeLayerFour]} />
  </View>
);

};

const renderHeroBackground = () => {
if (!displayedMedia?.url) {
return (
<View style={styles.defaultHeroBackground}>
<View style={styles.defaultHeroGlowOne} />
<View style={styles.defaultHeroGlowTwo} />
</View>
);
}

if (displayedMedia.type === 'video' && Platform.OS === 'web') {
  return (
    <View style={styles.heroMediaLayer}>
      {React.createElement('video', {
        src: displayedMedia.url,
        autoPlay: true,
        muted: true,
        loop: true,
        playsInline: true,
        controls: false,
        style: {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          border: '0px',
          outline: 'none',
          pointerEvents: 'none',
        },
      })}
    </View>
  );
}

if (displayedMedia.type === 'image') {
  return (
    <Image
      source={{ uri: displayedMedia.url }}
      style={styles.heroBackgroundImage}
      resizeMode="cover"
    />
  );
}

return (
  <View style={styles.defaultHeroBackground}>
    <View style={styles.defaultHeroGlowOne} />
    <View style={styles.defaultHeroGlowTwo} />
  </View>
);

};

if (loading) {
return (
<View style={styles.loadingPage}>
<ActivityIndicator size="large" color="#111" />
<Text style={styles.loadingText}>Loading voucher...</Text>
</View>
);
}

if (loadError) {
return (
<ScrollView style={styles.page} contentContainerStyle={styles.errorPageContent} >
<View style={styles.errorCard}>
<Ionicons name="alert-circle-outline" size={38} color="#111" />
<Text style={styles.errorTitle}>{loadError.title}</Text>
<Text style={styles.errorText}>{loadError.message}</Text>

      <View style={styles.debugBox}>
        <Text style={styles.debugLabel}>CollabID being sent</Text>
        <Text selectable style={styles.debugValue}>
          {String(CollabID || 'EMPTY')}
        </Text>
      </View>

      <View style={styles.debugBox}>
        <Text style={styles.debugLabel}>Backend response</Text>
        <Text selectable style={styles.debugValue}>
          {typeof loadError.raw === 'string'
            ? loadError.raw
            : JSON.stringify(loadError.raw, null, 2)}
        </Text>
      </View>

      <TouchableOpacity
        style={styles.retryButton}
        activeOpacity={0.85}
        onPress={fetchVoucherInformation}
      >
        <Text style={styles.retryButtonText}>Try again</Text>
      </TouchableOpacity>
    </View>
  </ScrollView>
);

}

if (!voucher) {
return (
<View style={styles.loadingPage}>
<Ionicons name="alert-circle-outline" size={34} color="#111" />
<Text style={styles.errorTitle}>Voucher not loaded</Text>
<Text style={styles.errorText}>
No voucher data is currently available.
</Text>
</View>
);
}

return (
<ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
<View style={styles.topSupportWrapper}>
<Text style={styles.topSupportText}>
Support your favourite creators with{' '}
<Text style={styles.logoCollab}>collab</Text>
<Text style={styles.logoBay}>bay</Text> ©
</Text>
</View>

  <View style={styles.heroCard}>
    {renderHeroBackground()}

    <View pointerEvents="none" style={styles.heroSoftDarkOverlay} />
    {renderRealGradientFade()}

    <View style={styles.heroContent}>
      <View style={styles.heroHeader}>
        <View style={styles.avatarStack}>
          {renderAvatar(
            voucher.BrandProfileImage,
            voucher.BrandName,
            styles.brandAvatar
          )}
          {renderAvatar(
            voucher.PromoterProfileImage,
            voucher.Promoter,
            styles.promoterAvatar
          )}
        </View>

        <View style={[styles.stockPill, soldOut && styles.soldOutPill]}>
          <View style={[styles.stockDot, soldOut && styles.soldOutDot]} />
          <Text
            style={[
              styles.stockPillText,
              soldOut && styles.soldOutText,
            ]}
          >
            {soldOut ? 'Sold out' : `${voucher.Stock} left`}
          </Text>
        </View>
      </View>

      <View style={styles.heroMainCopy}>
        <Text style={styles.heroVenue}>{voucher.BrandName}</Text>

        <Text style={styles.heroTitle}>{voucher.Discount}% off</Text>

        <Text style={styles.heroSubtitle}>
          when you spend £{voucher.OnSpend}
        </Text>

        <View style={styles.creatorLine}>
          <Text style={styles.creatorPrefix}>Shared by </Text>
          <Text style={styles.creatorName}>{voucher.Promoter}</Text>
          <View style={styles.blueTick}>
            <Ionicons name="checkmark" size={11} color="#fff" />
          </View>
        </View>
      </View>
    </View>
  </View>

  <View style={styles.paymentDivider}>
    <View style={styles.paymentDividerLine} />
    <Text style={styles.paymentDividerText}>Voucher payment</Text>
    <View style={styles.paymentDividerLine} />
  </View>

  <View style={styles.checkoutCard}>
    <View style={styles.checkoutTopRow}>
      <View style={styles.checkoutTitleBlock}>
        <View style={styles.checkoutLogoRow}>
          <Text style={styles.checkoutLogoCollab}>collab</Text>
          <Text style={styles.checkoutLogoBay}>bay</Text>
        </View>

        <Text style={styles.checkoutTitle}>Complete your voucher</Text>
      </View>
    </View>

    <View style={styles.paymentLookCard}>
      <View style={styles.paymentLookLeft}>
        <View style={styles.paymentIconCircle}>
          <Ionicons name="mail-outline" size={19} color="#111" />
        </View>

        <View style={styles.paymentLookCopy}>
          <Text style={styles.paymentLookTitle}>Email delivery</Text>
          <Text style={styles.paymentLookText}>
            Your voucher will be sent to your email after payment.
          </Text>
        </View>
      </View>

      <Ionicons name="lock-closed-outline" size={18} color="#777" />
    </View>

    {paymentSuccess ? (
      <View style={styles.resultCard}>
        <View style={styles.successHeader}>
          <View style={styles.successIcon}>
            <Ionicons name="checkmark" size={22} color="#fff" />
          </View>

          <View style={styles.successCopy}>
            <Text style={styles.resultTitle}>
              {paymentStatus === 'processing'
                ? 'Payment processing'
                : 'Payment received'}
            </Text>
            <Text style={styles.resultSubtitle}>
              Your voucher will be emailed once payment is confirmed.
            </Text>
          </View>
        </View>

        <View style={styles.resultRow}>
          <Text style={styles.resultLabel}>Paid</Text>
          <Text style={styles.resultValue}>£{FIXED_PRICE}.00</Text>
        </View>

        <View style={styles.resultRow}>
          <Text style={styles.resultLabel}>Delivery email</Text>
          <Text selectable style={styles.resultValue}>
            {paidEmail}
          </Text>
        </View>

        <Text style={styles.paymentConfirmationNote}>
          Please keep this page open until your voucher email arrives.
        </Text>
      </View>
    ) : (
      <>
        <View
          style={[
            styles.applePayWrapper,
            !applePayAvailable && styles.applePayWrapperHidden,
          ]}
        >
          <Text style={styles.applePayLabel}>Express checkout</Text>

          {Platform.OS === 'web'
            ? React.createElement('div', {
                id: APPLE_PAY_ELEMENT_ID,
                style: {
                  width: '100%',
                  minHeight: '48px',
                },
              })
            : null}

          <View style={styles.orDivider}>
            <View style={styles.orDividerLine} />
            <Text style={styles.orDividerText}>OR PAY BY CARD</Text>
            <View style={styles.orDividerLine} />
          </View>
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>Email</Text>
          <View style={styles.inputShell}>
            <Ionicons name="mail-outline" size={19} color="#777" />
            <TextInput
              style={styles.input}
              value={CustomerEmail}
              onChangeText={(value) => {
                setCustomerEmail(value);
                setPaymentError('');
              }}
              placeholder="customer@email.com"
              placeholderTextColor="#999"
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              editable={!processing}
            />
          </View>
        </View>

        <View style={styles.fixedPriceCard}>
          <View>
            <Text style={styles.fixedPriceLabel}>Price</Text>
            <Text style={styles.fixedPriceSubtext}>
              Fixed voucher access
            </Text>
          </View>

          <Text style={styles.fixedPriceValue}>£{FIXED_PRICE}</Text>
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>Payment details</Text>

          <View style={styles.stripeCardShell}>
            {Platform.OS === 'web'
              ? React.createElement('div', {
                  id: CARD_ELEMENT_ID,
                  style: {
                    width: '100%',
                    minHeight: '22px',
                  },
                })
              : null}
          </View>

          {!stripeReady && Platform.OS === 'web' ? (
            <View style={styles.paymentLoadingRow}>
              <ActivityIndicator size="small" color="#777" />
              <Text style={styles.paymentLoadingText}>
                Loading secure payment...
              </Text>
            </View>
          ) : null}
        </View>

        {paymentError ? (
          <View style={styles.paymentErrorBox}>
            <Ionicons
              name="alert-circle-outline"
              size={17}
              color="#b42318"
            />
            <Text style={styles.paymentErrorText}>{paymentError}</Text>
          </View>
        ) : null}

        <View style={styles.smallHorizontalLine} />

        <TouchableOpacity
          style={[
            styles.purchaseButton,
            (processing || soldOut || !stripeReady) &&
              styles.purchaseButtonDisabled,
          ]}
          activeOpacity={0.9}
          onPress={handleCardPayment}
          disabled={processing || soldOut || !stripeReady}
        >
          {processing ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <>
              <Text style={styles.purchaseButtonText}>
                {soldOut ? 'Sold out' : `Pay £${FIXED_PRICE}`}
              </Text>
              <View style={styles.purchaseButtonIcon}>
                <Ionicons name="arrow-forward" size={18} color="#111" />
              </View>
            </>
          )}
        </TouchableOpacity>

        <View style={styles.securePaymentFooter}>
          <Ionicons name="lock-closed-outline" size={13} color="#777" />
          <Text style={styles.securePaymentFooterText}>
            Secure payment processed by Stripe
          </Text>
        </View>
      </>
    )}
  </View>

  <View style={styles.footer}>
    <Text style={styles.footerText}>
      <Text style={styles.footerLink}>collabbay.com</Text> ©
    </Text>
  </View>
</ScrollView>

);
};

const styles = StyleSheet.create({
page: {
flex: 1,
backgroundColor: '#f4f4f2',
},
pageContent: {
padding: 18,
paddingBottom: 70,
width: '100%',
maxWidth: 920,
alignSelf: 'center',
},
errorPageContent: {
padding: 18,
paddingBottom: 70,
width: '100%',
maxWidth: 920,
alignSelf: 'center',
justifyContent: 'center',
flexGrow: 1,
},

loadingPage: {
flex: 1,
backgroundColor: '#f4f4f2',
alignItems: 'center',
justifyContent: 'center',
padding: 24,
},
loadingText: {
marginTop: 12,
fontSize: 14,
fontWeight: '800',
color: '#666',
},

errorCard: {
backgroundColor: '#fff',
borderRadius: 28,
padding: 20,
borderWidth: 1,
borderColor: '#e8e8e8',
},
errorTitle: {
marginTop: 10,
fontSize: 22,
fontWeight: '900',
color: '#111',
},
errorText: {
marginTop: 6,
fontSize: 14,
color: '#666',
lineHeight: 20,
},
debugBox: {
marginTop: 14,
backgroundColor: '#f7f7f7',
borderRadius: 16,
padding: 12,
borderWidth: 1,
borderColor: '#e8e8e8',
},
debugLabel: {
color: '#777',
fontSize: 12,
fontWeight: '900',
marginBottom: 6,
},
debugValue: {
color: '#111',
fontSize: 12,
fontWeight: '800',
lineHeight: 18,
},
retryButton: {
marginTop: 18,
backgroundColor: '#111',
borderRadius: 999,
paddingVertical: 12,
paddingHorizontal: 18,
alignItems: 'center',
},
retryButtonText: {
color: '#fff',
fontSize: 14,
fontWeight: '900',
},

topSupportWrapper: {
alignItems: 'center',
justifyContent: 'center',
marginBottom: 14,
paddingHorizontal: 8,
},
topSupportText: {
textAlign: 'center',
color: '#555',
fontSize: 12,
fontWeight: '700',
lineHeight: 17,
},
logoCollab: {
color: '#111',
fontWeight: '700',
},
logoBay: {
color: '#111',
fontWeight: '950',
},

heroCard: {
minHeight: 520,
borderRadius: 36,
overflow: 'hidden',
backgroundColor: '#111',
marginBottom: 14,
position: 'relative',
},
heroMediaLayer: {
...StyleSheet.absoluteFillObject,
backgroundColor: '#111',
},
heroBackgroundImage: {
...StyleSheet.absoluteFillObject,
width: '100%',
height: '100%',
},
defaultHeroBackground: {
...StyleSheet.absoluteFillObject,
backgroundColor: '#111',
},
defaultHeroGlowOne: {
position: 'absolute',
width: 260,
height: 260,
borderRadius: 130,
backgroundColor: 'rgba(253,240,93,0.22)',
top: -80,
right: -70,
},
defaultHeroGlowTwo: {
position: 'absolute',
width: 240,
height: 240,
borderRadius: 120,
backgroundColor: 'rgba(255,255,255,0.08)',
bottom: -80,
left: -80,
},
heroSoftDarkOverlay: {
...StyleSheet.absoluteFillObject,
backgroundColor: 'rgba(0,0,0,0.16)',
},

nativeFadeWrapper: {
...StyleSheet.absoluteFillObject,
},
nativeFadeLayer: {
position: 'absolute',
left: 0,
right: 0,
},
nativeFadeLayerOne: {
bottom: 0,
height: '30%',
backgroundColor: 'rgba(0,0,0,0.88)',
},
nativeFadeLayerTwo: {
bottom: '24%',
height: '24%',
backgroundColor: 'rgba(0,0,0,0.54)',
},
nativeFadeLayerThree: {
bottom: '44%',
height: '22%',
backgroundColor: 'rgba(0,0,0,0.28)',
},
nativeFadeLayerFour: {
bottom: '62%',
height: '18%',
backgroundColor: 'rgba(0,0,0,0.10)',
},

heroContent: {
flex: 1,
minHeight: 520,
padding: 22,
justifyContent: 'space-between',
position: 'relative',
zIndex: 2,
},
heroHeader: {
flexDirection: 'row',
alignItems: 'center',
justifyContent: 'space-between',
},
avatarStack: {
flexDirection: 'row',
alignItems: 'center',
},
avatarOuter: {
width: 64,
height: 64,
borderRadius: 32,
backgroundColor: '#fff',
padding: 3,
borderWidth: 2,
borderColor: '#fff',
},
brandAvatar: {
zIndex: 1,
},
promoterAvatar: {
marginLeft: -18,
zIndex: 2,
},
avatarImage: {
width: '100%',
height: '100%',
borderRadius: 32,
backgroundColor: '#ededed',
},
avatarFallback: {
width: '100%',
height: '100%',
borderRadius: 32,
backgroundColor: '#FDF05D',
alignItems: 'center',
justifyContent: 'center',
},
avatarFallbackText: {
fontSize: 16,
fontWeight: '900',
color: '#111',
},

stockPill: {
flexDirection: 'row',
alignItems: 'center',
backgroundColor: 'rgba(17,17,17,0.72)',
borderRadius: 999,
paddingVertical: 10,
paddingHorizontal: 14,
borderWidth: 1,
borderColor: 'rgba(255,255,255,0.20)',
},
soldOutPill: {
backgroundColor: 'rgba(255,255,255,0.92)',
borderColor: 'rgba(255,255,255,0.4)',
},
stockDot: {
width: 7,
height: 7,
borderRadius: 99,
backgroundColor: '#FDF05D',
marginRight: 7,
},
soldOutDot: {
backgroundColor: '#999',
},
stockPillText: {
fontSize: 13,
fontWeight: '900',
color: '#fff',
},
soldOutText: {
color: '#111',
},

heroMainCopy: {
paddingBottom: 8,
},
heroVenue: {
color: '#FDF05D',
fontSize: 15,
fontWeight: '900',
marginBottom: 8,
},
heroTitle: {
color: '#fff',
fontSize: 60,
lineHeight: 64,
fontWeight: '900',
letterSpacing: -2,
},
heroSubtitle: {
color: '#efefef',
fontSize: 22,
fontWeight: '800',
marginTop: 6,
},
creatorLine: {
flexDirection: 'row',
alignItems: 'center',
flexWrap: 'wrap',
marginTop: 20,
},
creatorPrefix: {
color: '#d8d8d8',
fontSize: 16,
fontWeight: '800',
},
creatorName: {
color: '#fff',
fontSize: 17,
fontWeight: '900',
},
blueTick: {
width: 18,
height: 18,
borderRadius: 9,
backgroundColor: '#1D9BF0',
alignItems: 'center',
justifyContent: 'center',
marginLeft: 7,
borderWidth: 1.5,
borderColor: '#fff',
},

paymentDivider: {
flexDirection: 'row',
alignItems: 'center',
marginTop: 2,
marginBottom: 14,
paddingHorizontal: 4,
},
paymentDividerLine: {
flex: 1,
height: 1,
backgroundColor: '#dcdcdc',
},
paymentDividerText: {
marginHorizontal: 12,
color: '#777',
fontSize: 11,
fontWeight: '900',
textTransform: 'uppercase',
letterSpacing: 1,
},

checkoutCard: {
backgroundColor: '#fff',
borderRadius: 30,
padding: 20,
marginBottom: 16,
borderWidth: 1,
borderColor: '#e9e9e9',
},
checkoutTopRow: {
marginBottom: 14,
},
checkoutTitleBlock: {
alignItems: 'flex-start',
},
checkoutLogoRow: {
flexDirection: 'row',
alignItems: 'center',
marginBottom: 8,
},
checkoutLogoCollab: {
color: '#111',
fontSize: 18,
fontWeight: '700',
letterSpacing: -0.4,
},
checkoutLogoBay: {
color: '#111',
fontSize: 18,
fontWeight: '950',
letterSpacing: -0.4,
},
checkoutTitle: {
fontSize: 28,
fontWeight: '900',
color: '#111',
letterSpacing: -0.8,
},

paymentLookCard: {
backgroundColor: '#f7f7f7',
borderRadius: 22,
padding: 14,
borderWidth: 1,
borderColor: '#ededed',
marginBottom: 16,
flexDirection: 'row',
alignItems: 'center',
justifyContent: 'space-between',
},
paymentLookLeft: {
flexDirection: 'row',
alignItems: 'center',
flex: 1,
},
paymentIconCircle: {
width: 42,
height: 42,
borderRadius: 21,
backgroundColor: '#FDF05D',
alignItems: 'center',
justifyContent: 'center',
marginRight: 12,
},
paymentLookCopy: {
flex: 1,
},
paymentLookTitle: {
color: '#111',
fontSize: 15,
fontWeight: '900',
},
paymentLookText: {
marginTop: 2,
color: '#777',
fontSize: 12,
fontWeight: '700',
},

applePayWrapper: {
overflow: 'hidden',
marginBottom: 16,
},
applePayWrapperHidden: {
height: 0,
opacity: 0,
marginBottom: 0,
},
applePayLabel: {
color: '#111',
fontSize: 14,
fontWeight: '900',
marginBottom: 8,
},
orDivider: {
flexDirection: 'row',
alignItems: 'center',
marginTop: 18,
},
orDividerLine: {
flex: 1,
height: 1,
backgroundColor: '#e5e5e5',
},
orDividerText: {
marginHorizontal: 10,
color: '#888',
fontSize: 10,
fontWeight: '900',
letterSpacing: 0.9,
},

inputGroup: {
marginBottom: 13,
},
inputLabel: {
fontSize: 14,
fontWeight: '900',
color: '#111',
marginBottom: 7,
},
inputShell: {
flexDirection: 'row',
alignItems: 'center',
backgroundColor: '#f8f8f8',
borderWidth: 1,
borderColor: '#e2e2e2',
borderRadius: 18,
paddingHorizontal: 14,
minHeight: 54,
},
input: {
flex: 1,
paddingVertical: 13,
paddingHorizontal: 10,
fontSize: 15,
color: '#111',
fontWeight: '700',
...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
},

fixedPriceCard: {
backgroundColor: '#fbfbfb',
borderRadius: 22,
borderWidth: 1,
borderColor: '#ededed',
padding: 15,
marginBottom: 14,
flexDirection: 'row',
alignItems: 'center',
justifyContent: 'space-between',
},
fixedPriceLabel: {
color: '#111',
fontSize: 15,
fontWeight: '900',
},
fixedPriceSubtext: {
marginTop: 2,
color: '#777',
fontSize: 12,
fontWeight: '700',
},
fixedPriceValue: {
color: '#111',
fontSize: 28,
fontWeight: '950',
letterSpacing: -0.8,
},

stripeCardShell: {
backgroundColor: '#f8f8f8',
borderWidth: 1,
borderColor: '#e2e2e2',
borderRadius: 18,
paddingHorizontal: 15,
paddingVertical: 17,
minHeight: 56,
justifyContent: 'center',
},
paymentLoadingRow: {
marginTop: 8,
flexDirection: 'row',
alignItems: 'center',
},
paymentLoadingText: {
marginLeft: 7,
fontSize: 12,
fontWeight: '700',
color: '#777',
},
paymentErrorBox: {
backgroundColor: '#fff2f0',
borderRadius: 14,
borderWidth: 1,
borderColor: '#f5c2bd',
paddingHorizontal: 12,
paddingVertical: 10,
marginBottom: 14,
flexDirection: 'row',
alignItems: 'center',
},
paymentErrorText: {
flex: 1,
marginLeft: 7,
fontSize: 12,
lineHeight: 17,
color: '#b42318',
fontWeight: '800',
},

smallHorizontalLine: {
height: 1,
backgroundColor: '#ededed',
marginBottom: 14,
},

purchaseButton: {
backgroundColor: '#111',
borderRadius: 999,
paddingVertical: 15,
paddingHorizontal: 18,
alignItems: 'center',
justifyContent: 'center',
flexDirection: 'row',
minHeight: 58,
},
purchaseButtonDisabled: {
opacity: 0.55,
},
purchaseButtonText: {
color: '#fff',
fontSize: 16,
fontWeight: '900',
marginRight: 12,
},
purchaseButtonIcon: {
width: 30,
height: 30,
borderRadius: 15,
backgroundColor: '#FDF05D',
alignItems: 'center',
justifyContent: 'center',
},
securePaymentFooter: {
marginTop: 12,
flexDirection: 'row',
alignItems: 'center',
justifyContent: 'center',
},
securePaymentFooterText: {
marginLeft: 5,
color: '#777',
fontSize: 11,
fontWeight: '800',
},

resultCard: {
backgroundColor: '#ecf8ef',
borderColor: '#c7ebd0',
borderWidth: 1,
borderRadius: 28,
padding: 18,
marginBottom: 2,
},
successHeader: {
flexDirection: 'row',
alignItems: 'center',
marginBottom: 14,
gap: 10,
},
successIcon: {
width: 42,
height: 42,
borderRadius: 21,
backgroundColor: '#0a7d28',
alignItems: 'center',
justifyContent: 'center',
},
successCopy: {
flex: 1,
},
resultTitle: {
fontSize: 20,
fontWeight: '900',
color: '#0a7d28',
},
resultSubtitle: {
marginTop: 2,
fontSize: 13,
fontWeight: '700',
color: '#43764f',
lineHeight: 18,
},
resultRow: {
flexDirection: 'row',
justifyContent: 'space-between',
gap: 12,
paddingVertical: 10,
borderBottomWidth: 1,
borderBottomColor: '#d4efd9',
},
resultLabel: {
fontSize: 13,
fontWeight: '900',
color: '#276b39',
},
resultValue: {
flex: 1,
fontSize: 13,
fontWeight: '900',
color: '#111',
textAlign: 'right',
},
paymentConfirmationNote: {
marginTop: 13,
color: '#43764f',
fontSize: 12,
lineHeight: 17,
fontWeight: '700',
},

footer: {
alignItems: 'center',
justifyContent: 'center',
paddingVertical: 18,
paddingHorizontal: 18,
},
footerText: {
textAlign: 'center',
color: '#777',
fontSize: 12,
fontWeight: '800',
},
footerLink: {
color: '#111',
fontWeight: '900',
},
});

export default Voucher;